/**
 * Risultati inseriti senza connessione: restano sul telefono e partono da soli.
 *
 * - usePendingSync: ogni 10 s (e appena il telefono torna online) prova a inviare
 *   i risultati in attesa, in ordine.
 * - Se nel frattempo un altro staff ha salvato quella partita, il risultato in attesa
 *   NON sovrascrive: viene scartato e compare un avviso con il punteggio presente.
 */
import { createContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  errMsg, GareApiError, gare, isNetworkError, pending, type GaraState, type PendingResult,
} from "@/lib/gare";

export const PendingCtx = createContext<Record<number, PendingResult>>({});

export function usePendingFor(gid: number): PendingResult[] {
  const all = useSyncExternalStore(pending.subscribe, pending.list);
  return all.filter((p) => p.gid === gid);
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function usePendingSync(
  onSent: (gid: number, state: GaraState) => void,
  notify: (kind: "success" | "error" | "info", msg: string) => void,
) {
  const cb = useRef({ onSent, notify });
  cb.current = { onSent, notify };
  useEffect(() => {
    let busy = false;
    const flush = async () => {
      if (busy || !pending.list().length) return;
      busy = true;
      try {
        for (const p of [...pending.list()]) {
          try {
            const s = await gare.result(p.gid, p.mid, p.games_a, p.games_b, p.version);
            pending.remove(p.gid, p.mid);
            cb.current.onSent(p.gid, s);
            cb.current.notify("success", "Risultato in attesa inviato");
          } catch (e) {
            if (isNetworkError(e)) break; // ancora senza rete: si riprova al prossimo giro
            if (e instanceof GareApiError && e.status === 401) break; // sessione: rientra col PIN, poi riparte
            pending.remove(p.gid, p.mid);
            const d = e instanceof GareApiError ? e.detail : {};
            // 409 con gli stessi numeri = la richiesta era già arrivata prima della caduta di rete
            if (e instanceof GareApiError && e.status === 409 && d.games_a === p.games_a && d.games_b === p.games_b) continue;
            cb.current.notify("error", `Un risultato in attesa non è stato inviato: ${errMsg(e)}. Controlla quella partita.`);
          }
        }
      } finally {
        busy = false;
      }
    };
    flush();
    const t = setInterval(flush, 10_000);
    window.addEventListener("online", flush);
    return () => {
      clearInterval(t);
      window.removeEventListener("online", flush);
    };
  }, []);
}
